import {DxGridOptions} from "./ui-options/dx-grid-options";
import {DxGridFilter} from "./filter/dx-grid-filter";
import {DxCoreUtils} from "../../dx-core/utiles/dx-core-utils";
import {DxGridRow} from "./dx-grid-row";
import * as XLSX from "xlsx";
import {DxNciDictionary} from "../../dx-nci-core/domain/dx-nci-dictionary";
import {DxGridToolbarActionType} from "./ui-options/toolbar/dx-grid-toolbar-action-type";
import {PitRequestCriteriaSort} from "../../pit-shared/domain/requests/pit-request-criteria-sort";
import {DxGridColumn} from "./ui-options/column/dx-grid-column";
import {PitRequestCriteriaFilter} from "../../pit-shared/domain/requests/pit-request-criteria-filter";
import {DxGridDataType} from "./dx-grid-data-type";
import {DxDateTime} from "../../dx-core/utiles/dx-core-datetime-utils";
import {DxGridPage} from "./dx-grid-page";
import {Observable} from "rxjs";
import {HttpClient, HttpResponse} from "@angular/common/http";
import {PitRequestBody} from "../../pit-shared/domain/requests/pit-request-body";
import {map} from "rxjs/operators";
import {PitGridPage} from "../../pit-shared/domain/pit-grid-page";
import {DxObjectMapper} from "../../dx-core/services/dx-object-mapper.service";
import {DxRequestOptions} from "../../dx-core/domain/http/dx-request-options";
import {KeyValue} from "@angular/common";
import {Constructor} from "typedjson/js/typedjson/types";
import {DxHttpDispatcher} from "../../dx-core/services/dx-http-dispatcher.service";


/** Класс группирует вспомогательные static-методы, обеспечивающие работу грида. */
export class DxGridUtils {
  /**
   * Выполняет проверку конфигурации грида на предмет критичных немсостыковок.
   * "Критичных" - означает что при наличии ошибки работа грида не возможна или возможно
   * на оснонии насроек по-умолчанию (заведомо валидных).
   * Чтоб не захламлять инициализацию компонента.
   *
   * @param {DxGridOptions} options - опции грида.
   * @return {string[]} - массив причин не-валидности или `null` если все валидно.
   */
  public static verifyOptions(options: DxGridOptions): string[] {
    if(!options) return ['Объект опций грида не задан'];
    if(options.useServerSideDataModel) {
      if(!options.dataSourceIdent) {
        let msg = 'Грид используется с серверной моделью днных, но датасорс не предоставлен! ';
        msg += 'Если используется серверная модель данных, то предоставление идента датасорса в опциях ';
        msg += 'и определение соотвествующего провайдера в модуле обязательно.';
        return [msg];
      }
    }

    // Для справочников пока применяется единый на всю систему сервис
    // поэтому он не указивается как провайдер в конфиге, и, пока, его наличие не проверяется.

    if(options.showToolbar && !options.externalToolbarHandler) {
      const buttons = options.toolbar.buttons.filter(btn => {
        return DxGridToolbarActionType.ADD === btn.action.type || DxGridToolbarActionType.COPY === btn.action.type;
      });
      if(buttons.length && !options.rowFactoryIdent) {
        let msg = 'Не задана фабрика инстансов строк! ';
        msg += 'Фабрика является обязательной для работы действий "Добавить", "Сохраненить" ';
        msg += 'Если у грида есть тулбар и на нем опеделены кнопки: Добавить, Копировать ';
        msg += 'то необходимо предоставить инстанс фабрики для генерации конкретных реализаций строк. ';
        msg += 'Если для грида используется внешний хэдлер для обработки действий на панели инструментов ';
        msg += 'то создание новых строк и копий должно выполнятся вне грида. ';
        msg += 'А их добавление в таблицу происходить через публичное API грида.';
        return [msg];
      }
    }

    if(options.useInlineEdit) {
      if(options.showToolbar && !options.externalToolbarHandler) {
        const buttons = options.toolbar.buttons.filter(btn => {
          return DxGridToolbarActionType.SAVE === btn.action.type;
        });
        if(buttons.length > 0 && !options.useServerSideDataModel) {
          let msg = 'Неверная модель данных грида!';
          msg += 'Если используется встроенное редактирование строк и на тулбар добавлена кнопка сохранить, ';
          msg += 'а также используется внутренняя обработка действий тулбара. ';
          msg += 'Если для грида используется внешний хэдлер для обработки действий на панели инструментов ';
          msg += 'то создание сохранение изменений должно выполнятся вне грида. ';
          return [msg];
        }
      }
    }
    else {
      const buttons = options.toolbar.buttons.filter(btn => {
        return DxGridToolbarActionType.ADD === btn.action.type
            || DxGridToolbarActionType.EDIT === btn.action.type
            || DxGridToolbarActionType.REMOVE === btn.action.type;
      });
      if(buttons.length && !options.modalsProviderIdent) {
        let msg = 'Не задан провайдер модальных окон для грида! ';
        msg += 'Если используется добавление и редактирование строк через модальные формы, ';
        msg += 'то предоставление провайдера модальных окон обязательно!';
        return [msg];
      }
    }
    return null;
  }



  /**
   * creates a shallow copy of the `rows` input and returns the sorted copy. this function
   * does not sort the `rows` argument in place
   */
  public static sortRows(rows: any[], dirs: Map<string, string>): any[] {
    if(!rows) return [];
    if(!dirs || !dirs.size) return [...rows];

    /**
     * record the row ordering of results from prior sort operations (if applicable)
     * this is necessary to guarantee stable sorting behavior
     */
    const rowToIndexMap = new Map<any, number>();
    rows.forEach((row, index) => rowToIndexMap.set(row, index));
    const temp = [...rows];

    // cache valueGetter and compareFn so that they
    // do not need to be looked-up in the sort function body
    const cachedDirs = [];
    dirs.forEach((value, key) => {
      const prop = key;
      const cachedDir = {
        prop,
        dir: value,
        valueGetter: DxCoreUtils.getterForProp(prop),
        compareFn: DxCoreUtils.orderByComparator
      };
      cachedDirs.push(cachedDir);
    });

    return temp.sort(function(rowA: any, rowB: any) {

      for(const cachedDir of cachedDirs) {
        // Get property and valuegetters for column to be sorted
        const { prop, valueGetter } = cachedDir;
        // Get A and B cell values from rows based on properties of the columns
        const propA = valueGetter(rowA, prop);
        const propB = valueGetter(rowB, prop);

        // Compare function gets five parameters:
        // Two cell values to be compared as propA and propB
        // Two rows corresponding to the cells as rowA and rowB
        // Direction of the sort for this column as SortDirection
        // Compare can be a standard JS comparison function (a,b) => -1|0|1
        // as additional parameters are silently ignored. The whole row and sort
        // direction enable more complex sort logic.
        const comparison = cachedDir.dir !== /*SortDirection.desc*/'desc' ? cachedDir.compareFn(propA, propB) : -cachedDir.compareFn(propA, propB);
        // Don't return 0 yet in case of needing to sort by next property
        if (comparison !== 0) return comparison;
      }

      if (!(rowToIndexMap.has(rowA) && rowToIndexMap.has(rowB))) return 0;

      /**
       * all else being equal, preserve original order of the rows (stable sort)
       */
      return rowToIndexMap.get(rowA) < rowToIndexMap.get(rowB) ? -1 : 1;
    });
  }


  public static filterRows(rows: any[], filters?: Map<string, DxGridFilter>): any[] {
    if(!rows) return [];
    if(!filters || !filters.size) return [...rows];
    const temp = [...rows];

    return temp.filter(function (row) {
      let counter = 0;
      let prop: string;
      let propValue: any;
      let propValueGetter: any;
      let filter: DxGridFilter;

      filters.forEach((value, key) => {
        // Иначе нихрена не понятно :(
        prop = key;
        filter = value;
        // Получаем значение проперти
        propValueGetter = DxCoreUtils.getterForProp(key);
        propValue = propValueGetter(row, key);
        // Чекаем фильтр
        if(filter.passValue(propValue)) counter++;
      });

      // Если строка удовлетворяет всем критериям - берем ее.
      return counter === filters.size;
    });
  }


  public static exportXlsx(gridOptions: DxGridOptions, rows: DxGridRow[],
                           filters?: Map<string, DxGridFilter>,
                           sorts?: Map<string, string>,
                           dicts?: Map<string, DxNciDictionary>): void {
    let data = [...rows];
    // Клиентская фильтрация
    if(filters) data = DxGridUtils.filterRows(data, filters);
    // Клиентская сортировка
    if(sorts) data = DxGridUtils.sortRows(data, sorts);

    // Генерируем дамми-строку заголовка
    const header: any = {};
    // и заполняем маппу реттеров для получения значений колонки из строки
    const getters: Map<string, any> = new Map();
    gridOptions.columns.forEach(column => {
      header[column.property] =  column.title;
      // Получаем геттер для колонки
      getters.set(column.property, DxCoreUtils.getterForProp(column.property));
    });

    // Исключаем из данных системные атрибуты (не являющиеся колонками грида).
    const body: any[] = [];
    let row: any;
    let propValueGetter: any;
    data.forEach(item => {
      row = {};
      gridOptions.columns.forEach(column => {
        propValueGetter = getters.get(column.property);
        if(column.dataType.isDictionary && dicts && dicts.has(column.dataType.dictIdent)) {
          const dict = dicts.get(column.dataType.dictIdent);
          const elemId = propValueGetter(item, column.property);
          row[column.property] = dict.elementIndex.get(elemId);
        } else {
          row[column.property] = propValueGetter(item, column.property);
        }
      });
      body.push(row);
    });
    data = [header].concat(body);

    // Создаем рабочий лист
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data, {skipHeader: true});
    // Создаем рабочую книгу
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Лист 1');
    // Записываем файил и вызываем его загрузку
    const fileName = gridOptions.title;
    XLSX.writeFile(wb, fileName + '.xlsx');
  }



  /**
   * Method merges sorting criteria-s to use it in some request!
   * @param sorts
   * @param gridOptions
   */
  public static getSortParams(sorts: Map<string, string>, gridOptions: DxGridOptions): PitRequestCriteriaSort[] {
    const sortArray: PitRequestCriteriaSort[] = [...gridOptions.hiddenCriteria.sorts];

    sorts.forEach((direction, property) => {
      const sort: PitRequestCriteriaSort = new PitRequestCriteriaSort();
      const column: DxGridColumn = gridOptions.columns.find(item => item.property === property);
      if (column.sortFilterField) sort.field = column.sortFilterField; else sort.field = property;
      sort.direction = direction;
      sort.caseSens = false;
      sortArray.push(sort);
    });

    return sortArray;
  }

  /**
   * Method merges filtering criteria-s to use it in some request!
   * @param filters
   * @param gridOptions
   */
  public static getFilterParams(filters: Map<string, DxGridFilter>, gridOptions: DxGridOptions): PitRequestCriteriaFilter[] {
    const filtersArray = [...gridOptions.hiddenCriteria.filters];

    filters.forEach((gridFilter, property) => {
      let filter: PitRequestCriteriaFilter = new PitRequestCriteriaFilter();
      const column: DxGridColumn = gridOptions.columns.find(item => item.property === property);
      if (column.sortFilterField) filter.field = column.sortFilterField; else filter.field = property;
      switch (column.dataType.typeIdent) {
        case DxGridDataType.DICTIONARY:
        case DxGridDataType.TEXT:
        case DxGridDataType.LINK:
          filter.condition = 'CONTAINS';
          filter.value = gridFilter.values[0];
          break;

        case DxGridDataType.DATE:
          const date = DxDateTime.strToDateTime(gridFilter.values[0], 'DD.MM.YYYY');

          if (date) {
            filter.condition = 'between';
            filter.values.push("'"+DxDateTime.dateTimeToStr(date,'YYYY-MM-DD')+"T00:00:00'");
            filter.values.push("'"+DxDateTime.dateTimeToStr(date,'YYYY-MM-DD')+"T23:59:59'");
          } else {
            filter = undefined;
          }
          break;

        default:
          filter.condition = '=';
          filter.value = gridFilter.values[0];
      }
      if (filter) {
        filter.filterValues = undefined;
        filtersArray.push(filter);
      }
    });

    return filtersArray;
  }

  /**
   * Generic request method to get DxGrid page by gridOptions.criteria,
   */
  public static fetchPage<T extends DxGridRow>( http: DxHttpDispatcher, mapper: DxObjectMapper,  httpOptions: DxRequestOptions, criteriaUrl: string,
                                                page: DxGridPage, filters: Map<string, DxGridFilter>, sorts: Map<string, string>, gridOptions: DxGridOptions,
                                                rowsKeyClazz: KeyValue<string, Constructor<T>>): Observable<DxGridPage> {
    const body: PitRequestBody = new PitRequestBody();
    gridOptions.criterias.forEach((value, key) => body[key] = value);
    body.criteria.page.pageIndex = page.getPageIndex();
    body.criteria.page.pageSize = page.getPageSize();
    body.criteria.sorts = DxGridUtils.getSortParams(sorts, gridOptions);
    body.criteria.filters = DxGridUtils.getFilterParams(filters, gridOptions);

    const jsonBody = mapper.serialize(body, PitRequestBody);

    return http.post(criteriaUrl, jsonBody, httpOptions)
                .pipe(
                  map((response: HttpResponse<any>) => {
                    const page = mapper.deserialize(response.body.page || response.body, PitGridPage);
                    page.pageData = mapper.deserializeASArray(response.body[rowsKeyClazz.key], rowsKeyClazz.value);
                    return page;
                  })
                );
  }
}
